/**
 * BoundaryGuard - Stage boundary clamping + obstacle collision.
 *
 * Ported from bumblebee BoundaryGuard. Clamps sprites to stage bounds
 * and pushes sprites out of obstacle rectangles.
 *
 * @see GitHub Issue #852
 */

export default class BoundaryGuard {
    /**
     * @param {number} stageWidth - Stage width (480 for ScratchJr)
     * @param {number} stageHeight - Stage height (360 for ScratchJr)
     * @param {Array} obstacles - [{x, y, width, height}]
     * @param {object} [callbacks] - {onBoundary(spriteId)}
     */
    constructor (stageWidth, stageHeight, obstacles, callbacks) {
        this._width = stageWidth;
        this._height = stageHeight;
        this._obstacles = obstacles || [];
        this._callbacks = callbacks || {};
        this._prevPositions = new Map();
        this._resolving = false;
    }

    /**
     * Check sprite position and clamp if out of bounds or in obstacle.
     * @param {object} sprite - {x, y, id?, element?}
     * @returns {object} Clamped {x, y}
     */
    checkAndClamp (sprite) {
        if (this._resolving) return {x: sprite.x, y: sprite.y};
        this._resolving = true;

        let {x, y} = sprite;
        let clamped = false;

        // Stage boundary clamping
        if (x < 0) { x = 0; clamped = true; }
        if (x > this._width) { x = this._width; clamped = true; }
        if (y < 0) { y = 0; clamped = true; }
        if (y > this._height) { y = this._height; clamped = true; }

        // Obstacle collision — push back to previous position
        for (const obs of this._obstacles) {
            if (x >= obs.x && x <= obs.x + obs.width &&
                y >= obs.y && y <= obs.y + obs.height) {
                const prev = this._prevPositions.get(sprite.id || 'default');
                if (prev) {
                    x = prev.x;
                    y = prev.y;
                }
                clamped = true;
                break;
            }
        }

        // Store position for next check
        this._prevPositions.set(sprite.id || 'default', {x, y});

        if (clamped && this._callbacks.onBoundary) {
            this._callbacks.onBoundary(sprite.id || 'default');
        }

        this._resolving = false;
        return {x, y};
    }

    /**
     * Reset stored positions.
     */
    reset () {
        this._prevPositions.clear();
    }

    destroy () {
        this._prevPositions.clear();
        this._callbacks = {};
    }
}
